import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

/**
 * PWAInstallPrompt — bottom-sheet "Install app" banner shown on Android Chrome
 * when the app is installable. Registers the service worker on mount.
 *
 * Behaviour:
 *   • Hidden if running as installed PWA (display-mode: standalone)
 *   • Hidden if dismissed in last 7 days (localStorage)
 *   • Shown 4s after page load to avoid being intrusive
 */
const DISMISS_KEY = 'guruji-pwa-dismissed-at';
const DISMISS_DAYS = 7;

export default function PWAInstallPrompt() {
  const [promptEvent, setPromptEvent] = useState(null);
  const [visible, setVisible] = useState(false);

  // Register service worker once
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const swUrl = '/service-worker.js';
    window.addEventListener('load', () => {
      navigator.serviceWorker.register(swUrl).catch(() => {
        // SW registration failed — non-blocking
      });
    });
  }, []);

  // Capture install prompt event
  useEffect(() => {
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (standalone) return; // already installed

    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 24 * 3600 * 1000) {
      return;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setPromptEvent(e);
      setTimeout(() => setVisible(true), 4000);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const install = async () => {
    if (!promptEvent) return;
    promptEvent.prompt();
    try { await promptEvent.userChoice; } catch (e) { /* noop */ }
    setVisible(false);
    setPromptEvent(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible || !promptEvent) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 bg-white border border-red-200 rounded-xl shadow-2xl p-3 flex items-center gap-3 animate-in slide-in-from-bottom-5"
    >
      <img
        src="/icons/icon-192.png"
        alt="Guruji"
        className="w-12 h-12 rounded-xl flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm text-gray-900 truncate">Install Guruji by Vidya365</p>
        <p className="text-xs text-gray-600 truncate">Fast offline access, daily current affairs</p>
      </div>
      <button
        onClick={install}
        data-testid="pwa-install-btn"
        className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-2 rounded-lg flex-shrink-0"
      >
        <Download size={14} /> Install
      </button>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="pwa-dismiss-btn"
        className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
      >
        <X size={16} />
      </button>
    </div>
  );
}
